import { REGISTRY_BLOCK, readFaqRows } from '@web-studio/shared-schemas';
import { EditableContent } from '../../edit/EditableContent';
import { LucideIcon } from '../../icons/LucideIcon';
import { resolveSectionLucideIcon } from '../../icons/ctaLucideMap';
import { pickBlockField } from '../../lib/localized';
import type { BlockRenderProps } from '../../types';

export function FaqBlock({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionIconKey = pickBlockField(block.content, 'sectionIcon', locale);
  const intro = pickBlockField(block.content, 'intro', locale);
  const items = readFaqRows(pickBlockField(block.content, 'faqItems', locale));
  const target = isActive ? activeEditTarget : null;
  const sectionIcon = resolveSectionLucideIcon(sectionIconKey, 'faq');
  const isLedger = block.registryBlockKey === REGISTRY_BLOCK.presentation.faqLedger;
  const arrangementClass = isLedger ? 'ws-faq--ledger' : 'ws-faq--accordion';
  const showIntro = Boolean(editable || intro.trim());

  const listBody =
    items.length > 0 ? (
      items.map((item, index) =>
        isLedger ? (
          <div key={`${item.question}-${index}`} className="ws-faq__row" role="listitem">
            <h3 className="ws-faq__question">{item.question}</h3>
            <p className="ws-faq__answer">{item.answer}</p>
          </div>
        ) : (
          <details
            key={`${item.question}-${index}`}
            className="ws-faq__item"
            name="ws-faq"
            open={index === 0}
          >
            <summary className="ws-faq__summary">
              <span className="ws-faq__question">{item.question}</span>
            </summary>
            <div className="ws-faq__panel">
              <p className="ws-faq__answer">{item.answer}</p>
            </div>
          </details>
        ),
      )
    ) : null;

  return (
    <section
      className={['ws-faq', arrangementClass, isActive ? 'ws-block--active' : '']
        .filter(Boolean)
        .join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      id="faq"
    >
      <div className="ws-faq__inner">
        <div className="ws-faq__header">
          <EditableContent
            as="span"
            fieldKey="sectionIcon"
            editable={editable}
            activeEditTarget={target}
            className="ws-faq__icon-hit"
          >
            <LucideIcon icon={sectionIcon} size="md" className="ws-faq__icon" />
          </EditableContent>
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-faq__title"
          >
            {sectionTitle}
          </EditableContent>
        </div>
        {showIntro ? (
          <EditableContent
            fieldKey="intro"
            editable={editable}
            activeEditTarget={target}
            className="ws-faq__intro"
          >
            {intro}
          </EditableContent>
        ) : null}
        {items.length > 0 || editable ? (
          <EditableContent
            as="div"
            fieldKey="faqItems"
            editable={editable}
            activeEditTarget={target}
            className="ws-faq__list"
            role={isLedger ? 'list' : undefined}
          >
            {listBody}
          </EditableContent>
        ) : null}
      </div>
    </section>
  );
}
